export interface DailyStreakProps {
  id: string;
  studentId: string;
  currentStreak: number;
  longestStreak: number;
  lastActivityDate: Date;
  updatedAt: Date;
}

export class DailyStreak {
  private readonly props: DailyStreakProps;

  constructor(props: DailyStreakProps) {
    this.props = props;
  }

  get id(): string {
    return this.props.id;
  }

  get studentId(): string {
    return this.props.studentId;
  }

  get currentStreak(): number {
    return this.props.currentStreak;
  }

  get longestStreak(): number {
    return this.props.longestStreak;
  }

  get lastActivityDate(): Date {
    return this.props.lastActivityDate;
  }

  /**
   * Check if streak is still active (last activity was today or yesterday)
   */
  isActive(): boolean {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const lastActivity = new Date(
      this.props.lastActivityDate.getFullYear(),
      this.props.lastActivityDate.getMonth(),
      this.props.lastActivityDate.getDate()
    );
    const diffDays = Math.floor(
      (today.getTime() - lastActivity.getTime()) / (1000 * 60 * 60 * 24)
    );
    return diffDays <= 1;
  }

  /**
   * Check if already logged activity today
   */
  hasActivityToday(): boolean {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const lastActivity = new Date(
      this.props.lastActivityDate.getFullYear(),
      this.props.lastActivityDate.getMonth(),
      this.props.lastActivityDate.getDate()
    );
    return today.getTime() === lastActivity.getTime();
  }

  /**
   * Check if streak is broken (no activity for 2+ days)
   */
  isBroken(): boolean {
    return !this.isActive();
  }

  /**
   * Calculate bonus XP for today (1 XP per day in streak, max 30)
   */
  getBonusXP(): number {
    return Math.min(this.props.currentStreak, 30);
  }

  /**
   * Get streak milestone (every 7 days)
   */
  getMilestone(): number | null {
    if (this.props.currentStreak % 7 === 0 && this.props.currentStreak > 0) {
      return this.props.currentStreak;
    }
    return null;
  }

  /**
   * Get milestone message
   */
  getMilestoneMessage(): string | null {
    const milestone = this.getMilestone();
    if (milestone) {
      return `¡${milestone} días consecutivos! 🔥`;
    }
    return null;
  }

  /**
   * Check if current streak is a personal best
   */
  isPersonalBest(): boolean {
    return (
      this.props.currentStreak === this.props.longestStreak &&
      this.props.currentStreak > 0
    );
  }

  /**
   * Get days until next milestone
   */
  getDaysUntilNextMilestone(): number {
    return 7 - (this.props.currentStreak % 7);
  }

  /**
   * Get streak display with fire emoji based on length
   */
  getStreakDisplay(): string {
    const streak = this.props.currentStreak;
    if (streak === 0) return '0';
    if (streak < 7) return `${streak} 🔥`;
    if (streak < 30) return `${streak} 🔥🔥`;
    return `${streak} 🔥🔥🔥`;
  }

  toPlainObject(): DailyStreakProps {
    return { ...this.props };
  }
}
